import React, { useState, useEffect } from 'react';
import MathText from '../../components/MathText';

// === МАГИЧЕСКИЙ АВТО-ФОРМАТЕР ===
const autoFormatMath = (text) => {
    if (!text) return '';
    if (text.includes('$')) return text; 
  
    return text
      .replace(/\b(\d+)\/(\d+)\b/g, '$\\frac{$1}{$2}$') 
      .replace(/([a-zA-Zа-яА-Я])\^(\d+)/g, '$$$1^{$2}$$') 
      .replace(/>=/g, '$\\ge$')                        
      .replace(/<=/g, '$\\le$')                        
      .replace(/!=/g, '$\\neq$');                      
};

const QuestionDraftForm = ({ index, initialData, onSave, onDiscard, subjects = [], topics = [] }) => {
    const [text, setText] = useState("");
    const [isEditingText, setIsEditingText] = useState(false);
    
    const [subject, setSubject] = useState("");
    const [classLevel, setClassLevel] = useState(5);
    const [difficulty, setDifficulty] = useState(1);
    const [topic, setTopic] = useState("");
    const [image, setImage] = useState(null);
    
    const [errors, setErrors] = useState({});
    
    // СТЕЙТ ДЛЯ РЕДАКТИРОВАНИЯ ОТВЕТОВ
    const [editingAnsIdx, setEditingAnsIdx] = useState(-1);
    
    const [answers, setAnswers] = useState([
        { text: '', is_correct: true },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false }
    ]);

    const answerLabels = ['А', 'Б', 'В', 'Г', 'Д'];

    useEffect(() => {
        if (initialData) {
            setText(initialData.question_text || initialData.text || "");
            
            if (initialData.default_subject) setSubject(initialData.default_subject);
            if (initialData.default_class_level) setClassLevel(initialData.default_class_level);
            if (initialData.default_difficulty) setDifficulty(initialData.default_difficulty);
            if (initialData.default_topic) setTopic(initialData.default_topic);

            if (initialData.answers && Array.isArray(initialData.answers) && initialData.answers.length > 0) {
                setAnswers(initialData.answers);
            } else if (initialData.options && Array.isArray(initialData.options)) {
                const aiAnswers = Array(5).fill({ text: '', is_correct: false }).map((_, idx) => ({
                    text: initialData.options[idx] || "",
                    is_correct: idx === 0 
                }));
                setAnswers(aiAnswers);
            }
        }
    }, [initialData]);

    const handleAnswerTextChange = (idx, newText) => {
        const updatedAnswers = [...answers];
        updatedAnswers[idx].text = newText;
        setAnswers(updatedAnswers);
        
        if (errors[`answer_${idx}`]) {
            setErrors({ ...errors, [`answer_${idx}`]: false });
        }
    };

    const handleCorrectAnswerChange = (selectedIndex) => {
        const updatedAnswers = answers.map((ans, idx) => ({
            ...ans,
            is_correct: idx === selectedIndex
        }));
        setAnswers(updatedAnswers);
    };

    const handleSaveClick = (e) => {
        e.preventDefault();
        
        const newErrors = {};
        if (!subject) newErrors.subject = true;
        if (!text.trim()) newErrors.text = true;
        
        let hasEmptyAnswers = false;
        answers.forEach((ans, idx) => {
            if (!ans.text.trim()) {
                newErrors[`answer_${idx}`] = true;
                hasEmptyAnswers = true;
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            alert("⚠️ Пожалуйста, заполните пустые ячейки (выделены красным) и выберите предмет!");
            return;
        }

        onSave({ text, subject, class_level: classLevel, difficulty, topic, image, answers }, index);
    };

    // ОБРАБОТЧИК ВСТАВКИ ИЗОБРАЖЕНИЯ (СКРИНШОТА) ИЗ БУФЕРА ОБМЕНА
    const handlePasteImage = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                setImage(blob);
                e.preventDefault();
                break;
            }
        }
    };

    return (
        <div className="bg-white rounded-[1rem] border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full overflow-hidden">
            
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-2 flex justify-between items-center text-white">
                <span className="text-xs font-bold flex items-center gap-1.5">
                    <span>✨</span> Вопрос #{index + 1}
                </span>
                <button 
                    onClick={() => onDiscard(index)}
                    className="text-white/70 hover:text-white hover:bg-white/20 px-2 py-1 rounded text-[10px] font-bold transition-colors"
                >
                    ✕ Удалить
                </button>
            </div>

            <div className="p-3 flex flex-col flex-1 gap-3">
                
                {/* ТЕКСТ ВОПРОСА С ФУНКЦИЕЙ ВСТАВКИ CTRL+V ВНУТРЬ БОКСА */}
                <div className={`min-h-[60px] rounded-lg p-2 border transition-colors ${errors.text ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-gray-100 bg-gray-50/50'}`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className={`text-[9px] font-black uppercase tracking-widest ${errors.text ? 'text-red-500' : 'text-gray-400'}`}>Текст вопроса</span>
                        {!isEditingText && (
                            <button onClick={() => setIsEditingText(true)} className="text-[10px] text-blue-500 font-bold hover:underline">
                                ✎ Изменить
                            </button>
                        )}
                    </div>
                    
                    {isEditingText ? (
                        <div className="space-y-2">
                            {/* ПАНЕЛЬ ИНСТРУМЕНТОВ (Только кнопка вставки таблицы) */}
                            <div className="flex flex-wrap items-center gap-2">
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        const tableTemplate = "\n$$ \\begin{array}{|c|c|c|} \\hline x & y & z \\\\ \\hline 1 & 2 & 3 \\\\ \\hline \\end{array} $$\n";
                                        setText(prev => prev + tableTemplate);
                                        if (errors.text) setErrors({ ...errors, text: false });
                                    }}
                                    className="bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 transition-colors"
                                >
                                    📊 Вставить таблицу
                                </button>
                            </div>

                            {/* ОБНОВЛЕННЫЙ БОКС ВВОДА: ТЕКСТ + КАРТИНКА ВНУТРИ ОДНОЙ РАМКИ */}
                            <div className="w-full rounded-lg border border-blue-300 focus-within:ring-2 focus-within:ring-blue-500/20 bg-white p-2 flex flex-col gap-2">
                                <textarea
                                    rows="3"
                                    className="w-full outline-none text-xs text-gray-800 bg-transparent resize-y"
                                    placeholder="Текст вопроса... (вставьте скриншот графика через Ctrl+V)"
                                    value={text}
                                    onChange={(e) => {
                                        setText(e.target.value);
                                        if (errors.text) setErrors({ ...errors, text: false });
                                    }}
                                    onPaste={handlePasteImage}
                                    autoFocus
                                />
                                
                                {/* ПРЕВЬЮ КАРТИНКИ ПРЯМО ВНУТРИ БОКСА */}
                                {image && (
                                    <div className="relative self-start inline-block border border-gray-200 rounded p-1 bg-gray-50 mt-1">
                                        <img src={URL.createObjectURL(image)} alt="Скриншот" className="max-h-32 object-contain rounded" />
                                        <button 
                                            type="button" 
                                            onClick={() => setImage(null)} 
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-md hover:bg-red-600 transition-colors"
                                            title="Удалить скриншот"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}
                            </div>

                            {text && (
                              <div className="px-2 py-1 bg-indigo-50/30 border border-indigo-100 rounded-lg text-xs text-gray-800 mt-1">
                                <span className="text-[9px] text-indigo-400 font-bold uppercase tracking-widest block mb-1">Предпросмотр текста:</span>
                                <MathText text={autoFormatMath(text)} />
                              </div>
                            )}
                            <button 
                                onClick={() => setIsEditingText(false)}
                                className="w-full py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-[10px] font-bold rounded uppercase tracking-widest mt-1 transition-colors"
                            >
                                Сохранить текст
                            </button>
                        </div>
                    ) : (
                        <div 
                            onClick={() => setIsEditingText(true)}
                            className={`text-xs font-semibold cursor-pointer min-h-[40px] p-2 bg-white rounded border border-transparent hover:border-gray-200 transition-colors ${errors.text ? 'text-red-700' : 'text-gray-800'}`}
                            title="Нажмите для редактирования"
                        >
                            <MathText text={autoFormatMath(text) || "Пустой вопрос"} />
                            {image && (
                                <div className="mt-2">
                                    <img src={URL.createObjectURL(image)} alt="Скриншот" className="max-h-24 object-contain rounded border border-gray-200 shadow-sm" />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* СЕЛЕКТОРЫ */}
                <div className="grid grid-cols-2 gap-2">
                    <select 
                        className={`w-full px-2 py-1.5 rounded-lg border bg-white outline-none font-bold text-[10px] transition-colors ${errors.subject ? 'border-red-500 text-red-700 ring-2 ring-red-200' : 'border-gray-200 text-gray-700'}`} 
                        value={subject} 
                        onChange={(e) => {
                            setSubject(e.target.value);
                            if (errors.subject) setErrors({ ...errors, subject: false });
                        }}
                    >
                        <option value="">Предмет...</option>
                        {subjects.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                    </select>
                    <select className="w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white outline-none font-bold text-[10px] text-gray-700" value={classLevel} onChange={(e) => setClassLevel(parseInt(e.target.value))}>
                        {[5, 6, 7, 8, 9, 10, 11].map(lvl => <option key={lvl} value={lvl}>{lvl} класс</option>)}
                    </select>
                    <select className="w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white outline-none font-bold text-[10px] text-gray-700" value={difficulty} onChange={(e) => setDifficulty(parseInt(e.target.value))}>
                        {[1, 2, 3, 4, 5].map(diff => <option key={diff} value={diff}>Сложность {diff}</option>)}
                    </select>
                    <select className="w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white outline-none font-bold text-[10px] text-gray-700" value={topic} onChange={(e) => setTopic(e.target.value)}>
                        <option value="">Без темы</option>
                        {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>

                {/* УМНЫЕ ВАРИАНТЫ ОТВЕТОВ */}
                <div className="space-y-2">
                    {answers.map((ans, idx) => (
                        <div key={idx} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border transition-colors ${
                            errors[`answer_${idx}`] 
                                ? 'border-red-500 bg-red-50 ring-2 ring-red-200' 
                                : ans.is_correct 
                                    ? 'border-green-400 bg-green-50' 
                                    : 'border-gray-200 hover:border-blue-200 bg-white'
                        }`}>
                            <input
                                type="radio"
                                name={`correct-answer-draft-${index}`}
                                checked={ans.is_correct}
                                onChange={() => handleCorrectAnswerChange(idx)}
                                className="w-3.5 h-3.5 text-green-600 focus:ring-green-500 cursor-pointer flex-shrink-0"
                            />
                            <span className={`text-[11px] font-black flex-shrink-0 ${
                                errors[`answer_${idx}`] ? 'text-red-500' : ans.is_correct ? 'text-green-700' : 'text-gray-400'
                            }`}>
                                {answerLabels[idx]})
                            </span>
                            
                            <div className="flex-1 min-w-0">
                                {editingAnsIdx === idx ? (
                                    <input
                                        type="text"
                                        className={`w-full bg-transparent border-b-2 border-blue-400 outline-none text-[11px] font-semibold pb-0.5 ${
                                            errors[`answer_${idx}`] ? 'text-red-700' : 'text-gray-800'
                                        }`}
                                        placeholder="Введите ответ..."
                                        value={ans.text}
                                        onChange={(e) => handleAnswerTextChange(idx, e.target.value)}
                                        autoFocus
                                        onBlur={() => setEditingAnsIdx(-1)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingAnsIdx(-1); }}
                                    />
                                ) : (
                                    <div 
                                        onClick={() => setEditingAnsIdx(idx)}
                                        className="cursor-pointer py-1 px-1 rounded min-h-[24px] flex items-center"
                                        title="Нажмите для редактирования"
                                    >
                                        {ans.text ? (
                                            <MathText text={autoFormatMath(ans.text)} />
                                        ) : (
                                            <span className="text-[10px] text-gray-400 italic">Нажмите, чтобы ввести ответ</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <button 
                    onClick={handleSaveClick} 
                    className="w-full py-2 mt-auto bg-blue-600 hover:bg-blue-700 text-white font-black text-[10px] rounded-lg uppercase tracking-widest transition-all shadow-sm"
                >
                    Сохранить в банк
                </button>
            </div>
        </div>
    );
};

export default QuestionDraftForm;